var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FinanceLayerParameter } from './FinanceLayerParameter';
import { FinanceDisclaimerMessages } from './FinanceDisclaimerMessages';
import { useBodyScrollLock, useVehicleRaw } from '../../../../hooks';
import { useServicesContext, useStockContext } from '../../../../context';
import { getDefaultProduct } from '../../../../components/finance/services/crs/api';
import { mergeDeep } from '../../../../components/finance/dynamic-finance/crs-helper';
import { mergeFSAGProduct } from '../../../../components/finance/services/crs/helpers';
import { Headline, ProgressIndicator } from '@oneaudi/unified-web-components';
import { SYS_BREAKPOINT_LG, SYS_BREAKPOINT_MD, SYS_SPACE_FIXED_300, } from '@oneaudi/unified-web-common';
import { readFinance } from '../../../utils';
import { FootnoteReferenceContextProvider } from '../../../../components';
const ParameterTable = styled.div `
  display: grid;

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    grid-template-columns: auto auto;
    column-gap: ${SYS_SPACE_FIXED_300};
  }
  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    max-width: 50%;
  }
`;
const ProgressIndicatorWrapper = styled.div `
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const FinanceLayerRethink = ({ isPlp, vehicleBasic }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    useBodyScrollLock();
    const { getConfiguration, getEnvironmentConfig, getAdditionalService } = useServicesContext();
    const logger = getAdditionalService('s2:logger');
    const trackingService = useServicesContext().getAdditionalService('audi-tracking-service');
    const financeSessionKey = ((_a = vehicleBasic.type) === null || _a === void 0 ? void 0 : _a.toUpperCase()) === 'N' ? 'VTP_FINANCING_NC' : 'VTP_FINANCING_UC';
    const [financing, setFinancing] = React.useState(undefined);
    const dispatch = isPlp ? useDispatch() : undefined;
    const fullVehicle = isPlp
        ? useVehicleRaw(vehicleBasic.id).vehicleRaw
        : useStockContext().vehicle;
    const hideCalculationDisclaimer = (_c = (_b = getConfiguration()) === null || _b === void 0 ? void 0 : _b.scopes) === null || _c === void 0 ? void 0 : _c.hideCalculationDisclaimer;
    const interpretDisclaimersTextStyle = (_e = (_d = getConfiguration()) === null || _d === void 0 ? void 0 : _d.scopes) === null || _e === void 0 ? void 0 : _e.interpretDisclaimersTextStyle;
    const hideFinancingTable = (_g = (_f = getConfiguration()) === null || _f === void 0 ? void 0 : _f.financeLayer) === null || _g === void 0 ? void 0 : _g.hideFinanceTable;
    const disclaimerListItems = (_j = (_h = getConfiguration()) === null || _h === void 0 ? void 0 : _h.financeLayer) === null || _j === void 0 ? void 0 : _j.disclaimerListItems;
    useEffect(() => {
        const getFinanceData = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c;
            try {
                const endpointUrl = (_a = getEnvironmentConfig()) === null || _a === void 0 ? void 0 : _a.crsServiceBaseUrl;
                let defaultProductResponse;
                if (isPlp) {
                    defaultProductResponse = yield getDefaultProduct(endpointUrl, mergeDeep(fullVehicle.detail.financingRequest.Request, fullVehicle.basic.financing
                        ? {
                            Product: fullVehicle.basic.financing.product,
                        }
                        : undefined), fullVehicle.basic.id, dispatch, trackingService);
                }
                if (!isPlp) {
                    const financeData = yield readFinance(financeSessionKey);
                    defaultProductResponse =
                        (_c = (_b = financeData === null || financeData === void 0 ? void 0 : financeData.financing[vehicleBasic.id]) === null || _b === void 0 ? void 0 : _b.financeResponseData) === null || _c === void 0 ? void 0 : _c.Response;
                }
                const newFinancing = mergeFSAGProduct(financing, defaultProductResponse, interpretDisclaimersTextStyle);
                setFinancing(newFinancing);
            }
            catch (e) {
                logger === null || logger === void 0 ? void 0 : logger.error('An error occcured while fetching finance data', e);
            }
        });
        if (fullVehicle) {
            getFinanceData();
        }
    }, [fullVehicle]);
    const footnoteReferenceServiceScopeManager = useServicesContext().getAdditionalService('audi-footnote-reference-service');
    const scopedFootnoteReferenceService = footnoteReferenceServiceScopeManager.getCustomScopeRefService('financeLayer');
    return (React.createElement(FootnoteReferenceContextProvider, { footnoteReferenceServiceScopeManager: footnoteReferenceServiceScopeManager, customScope: scopedFootnoteReferenceService.getConsumerId() }, financing ? (React.createElement(React.Fragment, null,
        !hideFinancingTable && (React.createElement(Headline, { tag: "h3", variant: { order: '3', type: 'headline', weight: 'normal' }, margin: [0, 0, SYS_SPACE_FIXED_300, 0] }, (_k = financing === null || financing === void 0 ? void 0 : financing.product) === null || _k === void 0 ? void 0 : _k.label)),
        disclaimerListItems && disclaimerListItems.length > 0 && (React.createElement(FinanceDisclaimerMessages, { financing: financing, hideFinancingTable: hideFinancingTable, hideCalculationDisclaimer: hideCalculationDisclaimer, interpretDisclaimersTextStyle: interpretDisclaimersTextStyle, position: "Top", disclaimerListItems: disclaimerListItems, isPlp: isPlp, vehicleId: vehicleBasic.id, vehicleType: vehicleBasic.type })),
        !hideFinancingTable && (React.createElement("div", null,
            React.createElement(ParameterTable, null, (_l = financing === null || financing === void 0 ? void 0 : financing.parameters) === null || _l === void 0 ? void 0 : _l.map((parameter, index) => (React.createElement(FinanceLayerParameter, { key: parameter.id, parameter: parameter, isLast: index === financing.parameters.length - 1 })))))),
        disclaimerListItems && disclaimerListItems.length > 0 && (React.createElement(FinanceDisclaimerMessages, { financing: financing, hideFinancingTable: hideFinancingTable, hideCalculationDisclaimer: hideCalculationDisclaimer, interpretDisclaimersTextStyle: interpretDisclaimersTextStyle, position: "Bottom", disclaimerListItems: disclaimerListItems, isPlp: isPlp, vehicleId: vehicleBasic.id, vehicleType: vehicleBasic.type })))) : (React.createElement(ProgressIndicatorWrapper, null,
        React.createElement(ProgressIndicator, { variant: "spinner", type: "infinite" })))));
};
